// Books console types. Report data shapes mirror the frozen fixtures in
// contracts/fixtures/reports/*.json exactly (see contracts/fixtures/README.md).
// The four client-surface reports reuse financials-component's own render
// types via a relative import (same package family, one render contract) —
// cash-flow has no client-surface renderer yet, so it is defined locally.

import type {
  BalanceSheetData as FinancialsBalanceSheetData,
  ByLocationData,
  PnlData,
  PyComparisonData,
} from "../../../financials-component/src/types.js";
import type { RunningBalanceMode } from "../../../packages/contracts/src/running-balance-mode";
// Annotation only (rd round 12, A4-1): the two soft brands tell the brand
// guard which fields carry an internal key rather than words. Type-only, so
// the circularity with ../types.ts (which imports ActivityBooking) is erased.
import type { AccountPath, CompanySlug } from "../types.js";

export type {
  ByLocationData,
  PnlData,
  PyComparisonData,
  ReportSnapshot,
} from "../../../financials-component/src/types.js";
export type { RunningBalanceMode };

/**
 * Closed-month balance sheets omit this. An open month may carry one
 * sentence (L2 renders it). The financials renderer ignores the field.
 */
export interface BalanceSheetData extends FinancialsBalanceSheetData {
  provisionalNotice?: string;
}

export interface CashFlowLine {
  label: string;
  amount: string;
  /**
   * rd15 A11: the counterpart accounts whose cash movement this line buckets,
   * emitted by the pipeline. A line with none has no account to open.
   */
  accounts?: string[];
}

export interface CashFlowSection {
  key: string;
  label: string;
  lines: CashFlowLine[];
  total: string;
}

export interface CashFlowData {
  currency: string;
  periodStart: string;
  periodEnd: string;
  openingCash: string;
  closingCash: string;
  sections: CashFlowSection[];
  netChange: string;
}

export interface ReportDataMap {
  pnl: PnlData;
  "balance-sheet": BalanceSheetData;
  "cash-flow": CashFlowData;
  "by-location": ByLocationData;
  "py-comparison": PyComparisonData;
}

export const REPORT_IDS = [
  "pnl",
  "balance-sheet",
  "cash-flow",
  "by-location",
  "py-comparison",
] as const;

export type ReportId = (typeof REPORT_IDS)[number];

export type TypicalSlot = "month" | "quarter" | "ytd" | "last_year";

export interface ReportWindowCatalogItem {
  id: string;
  company: CompanySlug;
  report: string;
  windowToken: string;
  windowStart?: string;
  windowEnd?: string;
  typicalSlot?: TypicalSlot | null;
  origin: "typical" | "requested";
  status: "pending" | "ready" | "failed";
  generatedAt?: string;
  contentType?: "application/json" | "text/csv";
}

export interface ReportWindowCatalog {
  company: CompanySlug;
  items: ReportWindowCatalogItem[];
}

export interface ReportWindowRequestResult {
  id: string;
  company: CompanySlug;
  report: string;
  windowToken: string;
  status: "pending" | "ready" | "failed";
}

export interface AdhocWindowEnvelope<TData = unknown> {
  apiVersion: "v1";
  kind: "adhoc-window";
  company: CompanySlug;
  report: string;
  window: string;
  windowStart: string;
  windowEnd: string;
  generatedAt: string;
  comparisonWindow?: string;
  comparisonWindowStart?: string;
  comparisonWindowEnd?: string;
  data: TData;
}

export interface ReportWindowDownload {
  contentType: string;
  filename: string;
  text: string;
  json?: unknown;
}

// --- General ledger --------------------------------------------------------

export interface GeneralLedgerEntry {
  date: string;
  account: AccountPath;
  payee?: string;
  narration: string;
  amount: string;
  runningBalance: string;
  location: string;
  source: string;
}

export interface GeneralLedgerQuery {
  period?: string;
  account?: AccountPath;
  location?: string;
  page?: number;
  pageSize?: number;
  runningBalanceMode?: RunningBalanceMode;
}

export interface GeneralLedgerPage {
  company: CompanySlug;
  period: string;
  entries: GeneralLedgerEntry[];
  page: number;
  pageSize: number;
  total: number;
  runningBalanceMode: RunningBalanceMode;
}

// --- Transactions (posted + queued browse; distinct from the review Queue) --

export type ConsoleTransactionStatus = "posted" | "queued";

export interface ConsoleTransaction {
  id: string;
  company: CompanySlug;
  date: string;
  amount: string;
  merchant?: string;
  descriptor?: string;
  account?: AccountPath;
  location?: string;
  status: ConsoleTransactionStatus;
  plaidTransactionId?: string;
  source?: string;
}

export interface TransactionsQuery {
  period?: string;
  status?: ConsoleTransactionStatus;
  page?: number;
  pageSize?: number;
  /** Keyset cursor date (YYYY-MM-DD); must pair with afterId (C-28). */
  afterDate?: string;
  /** Keyset cursor plaid transaction id; must pair with afterDate (C-28). */
  afterId?: string;
}

export interface ConsoleTransactionsPage {
  company: CompanySlug;
  transactions: ConsoleTransaction[];
  page: number;
  pageSize: number;
  total: number;
  /** Server-known keyset continuation; do not infer from page arithmetic. */
  has_more: boolean;
}

// --- Balances (Seam C shape, docs/design/hub-balances-seam.md) -------------

export interface BalanceAccount {
  name: string;
  type: string;
  /** Last-4 display label only; full account numbers are never stored/returned. */
  mask: string;
  currency: string;
  currentBalance: string;
  availableBalance?: string;
  creditLimit?: string;
  asOf: string;
  /**
   * Opaque Plaid ids (owner grant 2026-10-09). `accountId` is what a bank line
   * carries as `ActivityItem.account.id`, so it is the value the Activity
   * filter sends. Not a bank account number.
   */
  accountId?: string;
  plaidItemId?: string;
}

export interface BalancesSnapshot {
  company: CompanySlug;
  /** Null when no snapshot has been captured yet (empty-but-valid, never an error). */
  asOf: string | null;
  accounts: BalanceAccount[];
}

export interface BalanceHistoryPoint {
  asOf: string;
  currentBalance: string;
  availableBalance?: string;
  creditLimit?: string;
}

export interface BalanceHistory {
  company: CompanySlug;
  accountName: string;
  points: BalanceHistoryPoint[];
}

// --- Activity (GET /v1/admin/{company}/activity) ---------------------------
// Money-in-positive for both bank and Moov. A bank row has no fee, net, or moov.

export const ACTIVITY_ITEM_SOURCES = ["bank", "moov"] as const;
export type ActivityItemSource = (typeof ACTIVITY_ITEM_SOURCES)[number];

export const ACTIVITY_QUERY_SOURCES = ["all", "bank", "moov"] as const;
export type ActivityQuerySource = (typeof ACTIVITY_QUERY_SOURCES)[number];

export const ACTIVITY_STATES = [
  "received",
  "needs_you",
  "approved",
  "dismissed",
  "processed",
  "booked",
  // rd Z8: a card the account manager sent to the owner (owner 2026-10-28,
  // option A: "A"). Mirror of the frozen contract's ACTIVITY_STATES.
  "with_the_owner",
] as const;
export type ActivityState = (typeof ACTIVITY_STATES)[number];

export interface ActivityMoov {
  kind: string;
  property?: string;
  lease?: string;
}

export const BOOKED_BY = [
  "receipt",
  "rule",
  "ai",
  "system_match",
  "person",
] as const;
export type BookedBy = (typeof BOOKED_BY)[number];

/** rd15 A1: the bank account a bank line came from (name and last four only). */
export interface ActivityAccount {
  /** Opaque Plaid account id. Never an account number. */
  id: string;
  name: string;
  /** The last four characters only. */
  mask: string;
}

/** G7. Present only when the saved ledger copy proves one booking. */
export interface ActivityBooking {
  /** Omitted when the ledger source proves none of the five values. */
  by?: BookedBy;
  /** rd15 A2: the number in a `rule-N` source. Present only with by === "rule". */
  ruleId?: number;
  /**
   * rd15 A2: the staff login whose slug the source carries. There is no
   * display-name field, so the login is the name. Present only with
   * by === "person" and only when a staff row matches.
   */
  person?: string;
  accounts: AccountPath[];
  location?: string;
}

export interface ActivityItem {
  id: string;
  source: ActivityItemSource;
  date: string;
  amount: string;
  fee?: string;
  net?: string;
  description: string;
  state: ActivityState;
  refId: string;
  moov?: ActivityMoov;
  account?: ActivityAccount;
  booking?: ActivityBooking;
}

export interface ActivitySources {
  bank: { lastSyncedAt?: string };
  moov: { walletConfigured: boolean; lastReadAt?: string };
  ledger: { available: boolean; asOf?: string };
}

export interface ActivityPage {
  items: ActivityItem[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  sources: ActivitySources;
}

/**
 * rd15 A4: one month, or a from..to range when both dates are given (`period`
 * is then omitted). The other keys narrow the request on the server, so the
 * page's total and paging are the filtered view's own.
 */
export interface ActivityQuery {
  period?: string;
  from?: string;
  to?: string;
  /** A bank account id, from `ActivityItem.account.id`. */
  account?: string;
  /** A magnitude, never negative; `direction` says which way it reads. */
  minAmount?: string;
  maxAmount?: string;
  /** rd15 A4 fix round 1: money in, money out, or both (the default). */
  direction?: ActivityDirection;
  q?: string;
  source?: ActivityQuerySource;
  state?: ActivityState;
  page?: number;
  pageSize?: number;
}

/** The direction an amount window reads, as the Activity route names it. */
export type ActivityDirection = "in" | "out" | "both";

/** G9. One request across every month; q is required and trimmed server-side. */
export interface ActivitySearchQuery {
  q: string;
  source?: ActivityQuerySource;
  state?: ActivityState;
}

export interface ActivitySearchResponse {
  items: ActivityItem[];
  matchCount: number;
  capped: boolean;
  /** Present together; the months actually searched, newest first. */
  searchedFrom?: string;
  searchedTo?: string;
  allMonthsSearched: boolean;
  monthsWithoutBooksView: number;
}

// --- Ledger view (three read-only routes) ----------------------------------
// Postings and account opening/activity/closing use the ledger natural sign
// (debit positive, credit negative). view.cashDelta is money-in-positive.

export const LEDGER_NOT_PUBLISHED_REASONS = [
  "ledger_not_published",
  "ledger_view_not_enabled",
  "statements_unavailable",
] as const;
export type LedgerNotPublishedReason = (typeof LEDGER_NOT_PUBLISHED_REASONS)[number];

export const LEDGER_STATEMENT_REPORTS = [
  "pnl",
  "balance-sheet",
  "cash-flow",
  "by-location",
] as const;
export type LedgerStatementReport = (typeof LEDGER_STATEMENT_REPORTS)[number];

export type LedgerPeriodState = "open" | "closed";

export type LedgerEntryKind =
  | "tenant_payment"
  | "deposit"
  | "expense"
  | "transfer"
  | "fee"
  | "journal"
  | "other";

export interface LedgerIds {
  plaidId?: string;
  /** One id, or principal and fee separated by a single space. */
  moovId?: string;
  pairedPlaidId?: string;
  lease?: string;
  correctionOf?: string;
  correctionKind?: string;
}

export interface LedgerPosting {
  account: AccountPath;
  amount: string;
  location?: string;
}

export interface LedgerViewLine {
  label: string;
  amount: string;
}

export interface LedgerEntryView {
  kind: LedgerEntryKind;
  headline: string;
  subline?: string;
  cashAccount?: AccountPath;
  cashDelta?: string;
  lines: LedgerViewLine[];
}

export interface LedgerEntry {
  id: string;
  date: string;
  flag: string;
  payee?: string;
  narration?: string;
  source: string;
  location?: string;
  ids: LedgerIds;
  postings: LedgerPosting[];
  view: LedgerEntryView;
  /** Present only together with balanceAfter, and only for one exact account. */
  accountAmount?: string;
  balanceAfter?: string;
}

export interface LedgerPeriodSummary {
  period: string;
  entryCount?: number;
  state: LedgerPeriodState;
}

export interface LedgerHeader {
  company: CompanySlug;
  period: string;
  state: LedgerPeriodState;
  asOf: string;
  ledgerCommitAt: string;
  loaderErrors: number;
  waitingForReview: number;
  periods: LedgerPeriodSummary[];
}

export interface LedgerEntriesPage {
  header: LedgerHeader;
  entries: LedgerEntry[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface LedgerQuery {
  /** rd15 A10: optional when from and to are both given. */
  period?: string;
  q?: string;
  account?: AccountPath;
  /**
   * rd15 A11: several accounts in one read, which is what a cash-flow line
   * buckets. Sent as the one `account` query key, comma separated; a single
   * account keeps its running balance, several carry none.
   */
  accounts?: AccountPath[];
  accountPrefix?: AccountPath;
  location?: string;
  lease?: string;
  source?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

/** G9. At least one filter; an empty q does not count as a filter. */
export interface LedgerSearchQuery {
  q?: string;
  account?: AccountPath;
  accountPrefix?: AccountPath;
  location?: string;
  lease?: string;
  source?: string;
}

export interface LedgerSearchHit {
  period: string;
  state: LedgerPeriodState;
  entry: LedgerEntry;
}

export interface LedgerSearchResponse {
  company: CompanySlug;
  items: LedgerSearchHit[];
  matchCount: number;
  capped: boolean;
  /** Present together; the months actually searched, newest first. */
  searchedFrom?: string;
  searchedTo?: string;
  monthsSearched: number;
  allMonthsSearched: boolean;
  monthsUnreadable: number;
}

export interface LedgerAccount {
  account: AccountPath;
  type: string;
  openedOn?: string;
  opening: string;
  activity: string;
  closing: string;
  isCash: boolean;
}

export interface TrialBalance {
  totalDebits: string;
  totalCredits: string;
}

export interface LedgerAccountsResponse {
  header: LedgerHeader;
  accounts: LedgerAccount[];
  trialBalance: TrialBalance;
}

export interface LedgerStatement<TData = unknown> {
  apiVersion: "v1";
  company: CompanySlug;
  report: LedgerStatementReport;
  period: string;
  generatedAt: string;
  pipelineRunId: string;
  data: TData;
  state: LedgerPeriodState;
  asOf: string;
  throughDate: string;
}

// --- Month status (GET /v1/admin/{company}/months) --------------------------
// G8. Read-only stored facts; the page turns these keys into plain words.

export const MONTH_STATUSES = [
  "closed",
  "closed_check_failed",
  "reopened",
  "needs_you",
  "waiting",
  "open",
] as const;
export type MonthStatus = (typeof MONTH_STATUSES)[number];

export const MONTH_RECONCILED_VALUES = ["yes", "no", "not_yet"] as const;
export type MonthReconciled = (typeof MONTH_RECONCILED_VALUES)[number];

export const MONTH_BLOCKER_KEYS = [
  "close_blocked",
  "reclose_needed",
  "closed_month_write_blocked",
  "cards_to_review",
  "cards_with_owner",
  "cards_with_practice",
  "statements_not_matching",
  "statements_to_confirm",
  "statements_saving",
  "check_failed_after_close",
  "bank_lines_waiting",
  "bank_lines_failed",
] as const;
export type MonthBlockerKey = (typeof MONTH_BLOCKER_KEYS)[number];

export const MONTH_BLOCKER_TARGETS = [
  "to_review",
  "waiting",
  "statement_checks",
  "problems",
  "none",
] as const;
export type MonthBlockerTarget = (typeof MONTH_BLOCKER_TARGETS)[number];

export interface AdminMonthBlocker {
  key: MonthBlockerKey;
  count: number;
  target: MonthBlockerTarget;
}

export const MONTH_BOOKS_VIEWS = ["loaded", "not_loaded"] as const;
export type MonthBooksView = (typeof MONTH_BOOKS_VIEWS)[number];

export interface AdminMonth {
  period: string;
  status: MonthStatus;
  /** Present exactly when status is closed or closed_check_failed. */
  closedAt?: string;
  reconciled: MonthReconciled;
  accountsChecked: number;
  booksView: MonthBooksView;
  /** Present only when booksView is loaded. */
  booksAsOf?: string;
  blockers: AdminMonthBlocker[];
}

export const BOOKING_RUN_OUTCOMES = ["running", "succeeded", "failed"] as const;
export type BookingRunOutcome = (typeof BOOKING_RUN_OUTCOMES)[number];

export interface AdminMonthsLastBookingRun {
  startedAt: string;
  /** Absent exactly when outcome is running. */
  completedAt?: string;
  outcome: BookingRunOutcome;
}

export interface AdminMonthsResponse {
  company: CompanySlug;
  generatedAt: string;
  lastBookingRun: AdminMonthsLastBookingRun | null;
  /** Newest first, at most 24. */
  months: AdminMonth[];
  undatedBlockers: AdminMonthBlocker[];
}
